import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY_PREFIX = "thyroid_progress";
const EXPIRY_HOURS = 24;

/**
 * Shared hook for saving/restoring questionnaire progress to localStorage.
 * Works for both patient and doctor desktop dashboards plus mobile.
 *
 * @param {string} variant - "patient" | "doctor" | "mobile"
 * @param {object} userId - The current user ID
 * @param {object} data - { step, complaints, questionnaire, testValues, ultrasoundText }
 * @param {object} setters - { setStep, setComplaints, setQuestionnaire, setTestValues, setUltrasoundText }
 */
export function useProgressSave(variant, userId, data, setters) {
  const [lastSaved, setLastSaved] = useState(null);
  const [showRestoredBanner, setShowRestoredBanner] = useState(false);

  const storageKey = userId ? `${STORAGE_KEY_PREFIX}_${variant}_${userId}` : null;

  // Restore on mount
  useEffect(() => {
    if (!storageKey) return;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const savedTime = new Date(parsed.savedAt);
      const hoursDiff = (Date.now() - savedTime.getTime()) / (1000 * 60 * 60);

      if (hoursDiff >= EXPIRY_HOURS) {
        localStorage.removeItem(storageKey);
        return;
      }

      if (parsed.step) setters.setStep(parsed.step);
      if (parsed.complaints) setters.setComplaints(parsed.complaints);
      if (parsed.questionnaire) setters.setQuestionnaire(prev => ({ ...prev, ...parsed.questionnaire }));
      if (parsed.testValues) setters.setTestValues(parsed.testValues);
      if (parsed.ultrasoundText) setters.setUltrasoundText(parsed.ultrasoundText);

      setLastSaved(savedTime);
      setShowRestoredBanner(true);
      setTimeout(() => setShowRestoredBanner(false), 5000);
    } catch (e) {
      console.error("Failed to restore progress:", e);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Save (debounced via the effect below)
  const saveProgress = useCallback(() => {
    if (!storageKey) return;

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          step: data.step,
          complaints: data.complaints,
          questionnaire: data.questionnaire,
          testValues: data.testValues,
          ultrasoundText: data.ultrasoundText,
          savedAt: new Date().toISOString(),
        })
      );
      setLastSaved(new Date());
    } catch (e) {
      console.error("Failed to save progress:", e);
    }
  }, [storageKey, data.step, data.complaints, data.questionnaire, data.testValues, data.ultrasoundText]);

  // Auto-save 1 second after any data change
  useEffect(() => {
    const timer = setTimeout(saveProgress, 1000);
    return () => clearTimeout(timer);
  }, [saveProgress]);

  // Clear progress (call after successful submit)
  const clearProgress = useCallback(() => {
    if (storageKey) localStorage.removeItem(storageKey);
    setLastSaved(null);
  }, [storageKey]);

  return { lastSaved, showRestoredBanner, setShowRestoredBanner, clearProgress };
}
